import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DatabaseZap } from 'lucide-react'
import { api, DB_UPDATE_PENDING_EVENT } from '@/api/client'
import { useAuth } from '@/hooks/useAuth'

/**
 * One clear notice for administrators when the server's database is behind the deployed code
 * (pending migrations), instead of scattered save failures (owner-reported, 2026-10-01). Only
 * HR_ADMIN / SUPER_ADMIN ask; everyone else never sees it. Checked once per session.
 */
export function MigrationBanner() {
  const { user, hasRole } = useAuth()
  const admin = !!user && hasRole(['HR_ADMIN'])
  const q = useQuery({
    queryKey: ['system', 'migrations'],
    queryFn: async () => (await api.get('/api/v1/system/migrations')).data as { migrations_pending: number | null },
    enabled: admin,
    staleTime: 30 * 60_000,
    retry: false,
    meta: { inlineError: true },
  })
  // QA-M01: everyone else learns of it the first time a request answers DATABASE_UPDATE_PENDING.
  const [hit, setHit] = useState(false)
  useEffect(() => {
    const on = () => setHit(true)
    window.addEventListener(DB_UPDATE_PENDING_EVENT, on)
    return () => window.removeEventListener(DB_UPDATE_PENDING_EVENT, on)
  }, [])
  const n = q.data?.migrations_pending ?? 0
  if (!admin && hit) {
    return (
      <div role="status" data-testid="migration-banner" className="flex items-start gap-2 border-b border-warning/30 bg-warning-subtle px-4 py-2 text-sm text-warning-content print:hidden">
        <DatabaseZap className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span><span className="font-semibold">Some features are being updated.</span> A few sections are unavailable until the server finishes an update. Your administrator has been told; nothing you entered was lost.</span>
      </div>
    )
  }
  if (!admin || !n) return null
  return (
    <div role="alert" data-testid="migration-banner" className="flex items-start gap-2 border-b border-warning/30 bg-warning-subtle px-4 py-2 text-sm text-warning-content print:hidden">
      <DatabaseZap className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>
        <span className="font-semibold">The server needs an update.</span> Its database is {n} change{n === 1 ? '' : 's'} behind this version of the app,
        so some saves will fail. Ask whoever maintains the server to take a backup and run the pending database migration.
      </span>
    </div>
  )
}
